import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Scale, X, Search } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ComplianceNote from "@/components/ComplianceNote";
import PackageCard from "@/components/PackageCard";
import CompareCountries from "@/components/CompareCountries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { CATEGORY_ORDER } from "@/lib/constants";
import { setSeo, SITE_URL } from "@/lib/seo";
import { useTranslation, Trans } from "react-i18next";

export default function Catalog() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [active, setActive] = useState("All");
  const [compareSel, setCompareSel] = useState([]);
  const [q, setQ] = useState("");
  const [regionSel, setRegionSel] = useState("All");
  const [regionOrder, setRegionOrder] = useState([]);
  const [regionByName, setRegionByName] = useState({});

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {});
    api.get("/catalog").then((r) => {
      const c = r.data.countries || [];
      setRegionOrder((r.data.region_order || []).filter((rg) => c.some((x) => x.region === rg)));
      setRegionByName(Object.fromEntries(c.map((x) => [x.name, x.region])));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rg = params.get("region");
    if (rg && regionOrder.includes(rg)) { setRegionSel(rg); setActive("Incorporation"); }
    const cat = params.get("category");
    if (cat && CATEGORY_ORDER.includes(cat)) setActive(cat);
  }, [regionOrder]);

  useEffect(() => {
    if (regionSel !== "All") {
      setSeo({ title: t("catalog.seoRegionTitle", { region: regionSel }), description: t("catalog.seoRegionDesc", { region: regionSel }), url: `${SITE_URL}/company-formation?region=${encodeURIComponent(regionSel)}` });
    } else {
      setSeo({ title: t("catalog.seoTitle"), description: t("catalog.seoDesc"), url: `${SITE_URL}/company-formation` });
    }
  }, [regionSel, t, i18n.language]);

  const categories = ["All", ...CATEGORY_ORDER];
  const ql = q.trim().toLowerCase();
  let visible = active === "All" ? packages : packages.filter((p) => p.category === active);
  if (regionSel !== "All") visible = packages.filter((p) => p.category === "Incorporation" && regionByName[p.name] === regionSel);
  if (ql) visible = visible.filter((p) => (`${p.name} ${p.tagline || ""}`).toLowerCase().includes(ql));
  const slugOf = (pkg) => pkg.id.replace("inc_", "");
  const nameOf = (slug) => (packages.find((p) => p.id === `inc_${slug}`)?.name) || slug;

  const toggleCompare = (pkg) => {
    const slug = slugOf(pkg);
    setCompareSel((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= 4) {
        toast.error(t("catalog.max4"));
        return prev;
      }
      return [...prev, slug];
    });
  };

  const goCompare = () => navigate(`/compare?c=${compareSel.join(",")}`);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain overflow-hidden border-b border-border bg-secondary/40">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-700">{t("catalog.kicker")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-foreground">{t("catalog.h1")}</h1>
            <p className="mt-4 text-muted-foreground">{t("catalog.lead")}</p>
            <Button variant="outline" className="mt-5 rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={() => navigate("/advisor")} data-testid="catalog-advisor-btn">
              <Sparkles className="h-4 w-4 mr-1.5 text-orange-700" /> {t("catalog.aiBtn")}
            </Button>
            <Button variant="outline" className="mt-5 sm:ml-3 rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={() => navigate("/compare")} data-testid="catalog-compare-btn">
              <Scale className="h-4 w-4 mr-1.5 text-orange-700" /> {t("catalog.compareBtn")}
            </Button>
          </motion.div>

          <div className="mt-8 max-w-3xl"><ComplianceNote /></div>

          <div className="mt-8 flex flex-col gap-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("catalog.search")} className="pl-9 bg-card border-border" data-testid="catalog-search" />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
              {categories.map((c) => (
                <button key={c} onClick={() => { setActive(c); setRegionSel("All"); }} className={`rounded-sm px-4 py-1.5 text-sm border transition-colors ${active === c && regionSel === "All" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-orange-300 hover:text-foreground"}`} data-testid={`catalog-filter-${c.toLowerCase()}`}>{t(`catalog.cat.${c}`, { defaultValue: c })}</button>
              ))}
            </div>
            {regionOrder.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="catalog-region-filters">
                <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mr-1">{t("catalog.region")}</span>
                <button onClick={() => setRegionSel("All")} className={`rounded-sm px-3 py-1 text-xs border transition-colors ${regionSel === "All" ? "bg-secondary text-primary border-border" : "bg-card text-muted-foreground border-border hover:text-foreground"}`} data-testid="catalog-region-all">{t("catalog.allRegions")}</button>
                {regionOrder.map((rg) => (
                  <button key={rg} onClick={() => { setRegionSel(rg); setActive("Incorporation"); }} className={`rounded-sm px-3 py-1 text-xs border transition-colors ${regionSel === rg ? "bg-orange-50 text-orange-700 border-orange-300" : "bg-card text-muted-foreground border-border hover:text-foreground"}`} data-testid={`catalog-region-${rg.toLowerCase().replace(/[^a-z]/g, "")}`}>{rg}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {active === "Incorporation" && (
          <p className="mb-6 text-sm text-muted-foreground"><Trans i18nKey="catalog.tickCompare" components={{ b: <span className="text-foreground font-medium" /> }} /></p>
        )}
        {visible.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="catalog-no-results">{t("catalog.noResults")}</div>
        ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
          {visible.map((pkg, i) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              index={i}
              compareToggle={pkg.category === "Incorporation" ? toggleCompare : undefined}
              compareChecked={compareSel.includes(slugOf(pkg))}
            />
          ))}
        </div>
        )}
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="flex items-center gap-2 mb-2">
            <Scale className="h-4 w-4 text-orange-700" />
            <p className="text-xs uppercase tracking-[0.2em] text-orange-700">{t("catalog.compareKicker")}</p>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-foreground mb-8">{t("catalog.compareH2")}</h2>
          <CompareCountries compact />
        </div>
      </section>
      <Footer />

      {compareSel.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl rounded-2xl border border-border bg-card card-soft shadow-lg px-4 py-3 flex items-center gap-3"
          data-testid="compare-tray"
        >
          <div className="flex flex-wrap gap-2 flex-1">
            {compareSel.map((slug) => (
              <span key={slug} className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-foreground" data-testid={`compare-tray-chip-${slug}`}>
                {nameOf(slug)}
                <button onClick={() => setCompareSel((p) => p.filter((s) => s !== slug))} className="text-muted-foreground hover:text-foreground" data-testid={`compare-tray-remove-${slug}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <Button
            onClick={goCompare}
            disabled={compareSel.length < 2}
            className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 shrink-0"
            data-testid="compare-tray-go"
          >
            {t("catalog.compareN", { n: compareSel.length })}
          </Button>
        </motion.div>
      )}
    </div>
  );
}
